import type { FC, ReactNode } from 'react'
import type { CategoryLink, ProductSummary } from '@/types'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { PackageOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toPersianDigits } from '@/lib/format'
import { ProductGrid } from '@/components/catalog/product-grid'
import { buttonVariants } from '@/components/ui/button'

type SortKey = 'recommended' | 'price-asc' | 'price-desc'

const SORT_OPTIONS: Array<{ value: SortKey, label: string }> = [
    { value: 'recommended', label: 'پیشنهاد نارون' },
    { value: 'price-asc', label: 'ارزان‌ترین' },
    { value: 'price-desc', label: 'گران‌ترین' }
]

const sortProducts = (products: ProductSummary[], sort: SortKey): ProductSummary[] => {
    if (sort === 'recommended') return products

    const priced = products.filter((product) => product.price !== null)
    const unpriced = products.filter((product) => product.price === null)
    const direction = sort === 'price-asc' ? 1 : -1

    priced.sort((first, second) => ((first.price ?? 0) - (second.price ?? 0)) * direction)

    return [...priced, ...unpriced]
}

const chipClassName = (isActive: boolean): string => cn(
    'inline-flex h-10 items-center rounded-full border px-4 text-sm font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
    isActive
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-border bg-card text-muted-foreground hover:border-foreground/30 hover:text-foreground'
)

interface CatalogViewProps {
    products: ProductSummary[]
    categories: CategoryLink[]
    activeCategoryId?: string | null
    unavailable?: boolean
}

export const CatalogView: FC<CatalogViewProps> = ({
    products,
    categories,
    activeCategoryId = null,
    unavailable = false
}): ReactNode => {
    const [sort, setSort] = useState<SortKey>('recommended')
    const sortedProducts = useMemo(() => sortProducts([...products], sort), [products, sort])

    return (
        <div>
            <div className='flex flex-col gap-5 border-b border-border pb-6 lg:flex-row lg:items-center lg:justify-between'>
                <nav aria-label='دسته‌بندی محصولات' className='no-scrollbar -mx-5 overflow-x-auto px-5 sm:mx-0 sm:px-0'>
                    <ul className='flex w-max gap-2'>
                        <li>
                            <Link
                                href='/products'
                                aria-current={activeCategoryId === null ? 'page' : undefined}
                                className={chipClassName(activeCategoryId === null)}
                            >
                                همهٔ محصولات
                            </Link>
                        </li>

                        {categories.map((category) => (
                            <li key={category.id}>
                                <Link
                                    href={`/collections/${category.slug}`}
                                    aria-current={category.id === activeCategoryId ? 'page' : undefined}
                                    className={chipClassName(category.id === activeCategoryId)}
                                >
                                    <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                        {category.name}
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center justify-between gap-4 lg:justify-end'>
                    <p className='text-sm text-muted-foreground' aria-live='polite'>
                        {toPersianDigits(products.length)} محصول
                    </p>

                    <label className='flex items-center gap-3 text-sm'>
                        <span className='text-muted-foreground'>
                            مرتب‌سازی
                        </span>

                        <select
                            value={sort}
                            onChange={(event) => setSort(event.target.value as SortKey)}
                            className='h-10 rounded-full border border-input bg-card px-4 text-sm text-foreground outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30'
                        >
                            {SORT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            </div>

            {sortedProducts.length > 0 ? (
                <ProductGrid products={sortedProducts} className='mt-10' />
            ) : (
                <div className='mt-10 flex flex-col items-center rounded-3xl border border-dashed border-border bg-card px-6 py-16 text-center'>
                    <PackageOpen className='size-10 text-muted-foreground' aria-hidden='true' />

                    <h2 className='mt-5 font-heading text-2xl font-semibold'>
                        {unavailable ? 'نمایش محصولات موقتاً ممکن نیست' : 'هنوز محصولی در این دسته نیست'}
                    </h2>

                    <p className='mt-3 max-w-md text-sm leading-7 text-muted-foreground'>
                        {unavailable
                            ? 'لطفاً چند دقیقهٔ دیگر دوباره سر بزنید یا برای دیدن کاتالوگ کامل با مشاوران ما تماس بگیرید.'
                            : 'محصولات جدید این دسته به‌زودی اضافه می‌شوند. تا آن زمان می‌توانید سفارش اختصاصی ثبت کنید.'}
                    </p>

                    <div className='mt-6 flex flex-wrap justify-center gap-3'>
                        {activeCategoryId !== null && (
                            <Link href='/products' className={buttonVariants({ variant: 'outline' })}>
                                مشاهدهٔ همهٔ محصولات
                            </Link>
                        )}

                        <Link href='/contact' className={buttonVariants()}>
                            درخواست مشاوره
                        </Link>
                    </div>
                </div>
            )}
        </div>
    )
}
